import { CSSObject, Theme } from '@mui/material'
import { Row } from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { useMergeFormData } from 'components/Form'
import { Table, usePagination } from 'components/Table'
import { PaginationProps } from 'components/Table/Pagination'
import { useSort } from 'components/Table/Sort'
import { useErrorsOnPagesInfo } from 'features/globalSetup/hooks/useErrorsOnPagesInfo'
import { useFooterInfoProps } from 'features/globalSetup/hooks/useFooterInfoProps'
import { calculatePagination } from 'providers/tanstack-query/table'
import { Schema } from '../../form/schema'
import { AdUnitBidderSlice, useGetAdUnitBidders } from '../../hooks/UseGetAdUnitBidders'
import { useColumns } from './columns/useColumns'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { useCollectInventoryConnectionDirtyRows } from './hooks/useCollectInventoryConnectionDirtyRows'
import { AdUnitsForTable, AdUnitSortBy, AdUnitsTableProps, Params, paramsDefaults } from './types'
import { adUnitBiddersToSchema, getRequiredParamsNames } from './utils'

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const AdUnitsTable = ({ domainBidder, filters }: AdUnitsTableProps): React.JSX.Element => {
  const [params, setParams] = useState<Params>(paramsDefaults)

  const {
    domain: { id: domainId },
    workspaceBidder: {
      bidder: { s2sAvailable },
      status,
    },
  } = domainBidder

  const { page, rowsPerPage, setPage, setRowsPerPage } = usePagination({
    resetOnChange: [domainId, filters],
  })
  const { props: sortProps, sort } = useSort<AdUnitsForTable, AdUnitSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { adUnitBidders, count, loading } = useGetAdUnitBidders({
    domainBidder,
    ...calculatePagination({ page, rowsPerPage }),
    filters,
    sort,
  })

  useEffect(() => {
    if (adUnitBidders.length === 0) return

    const { csParams, s2sParams } = adUnitBidders[0]
    const csRequiredParamsNames = csParams ? getRequiredParamsNames(csParams) : []
    const s2sRequiredParamsNames = s2sParams ? getRequiredParamsNames(s2sParams) : []

    setParams({
      cs: { csParams, csRequiredParamsNames },
      s2s: { s2sParams: s2sAvailable ? s2sParams : undefined, s2sRequiredParamsNames },
    })
  }, [adUnitBidders])

  const columns = useColumns({ ...params, adUnitBidders, workspaceStatus: status })

  useMergeFormData<SchemaSlice>({
    data: adUnitBiddersToSchema(adUnitBidders),
    path: 'adUnitBidders',
    resetDependency: domainId,
    resetValue: {},
  })

  useCollectInventoryConnectionDirtyRows()

  const paginationProps: PaginationProps = {
    count,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
  }

  const { pagesWithError } = useErrorsOnPagesInfo<SchemaSlice>({
    current: { page, rowsPerPage },
    ids: adUnitBidders.map((el: AdUnitBidderSlice): string => el.id),
    schemaPath: 'adUnitBidders',
    updateDependency: adUnitBidders,
  })
  const footerInfoProps = useFooterInfoProps({ pagesWithError })

  const handleStyleRow = (row: Row<AdUnitsForTable>, theme: Theme): CSSObject => {
    const {
      original: {
        adUnit: { ybAdUnitEnabled },
      },
    } = row

    let styles: CSSObject = {}
    if (ybAdUnitEnabled) {
      styles = {
        ...styles,
        backgroundColor: theme.palette.cream.main,
      }
    }

    return styles
  }

  return (
    <Table<AdUnitsForTable>
      columns={columns}
      data={adUnitBidders}
      footerInfo={footerInfoProps}
      loading={loading || params.cs.csParams === undefined}
      meta={{
        stickyHeader: true,
        styleRow: handleStyleRow,
        verticalBorders: true,
      }}
      pagination={paginationProps}
      sort={sortProps}
    />
  )
}
